import type { UIMessage } from 'ai';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';

import { advisorMessageFeedbackRating, advisorUiMessageWithMetadataPatch } from './advisor-v2-chat-history';

export type AgentSubmitMessageFeedbackFn = (
  messageId: string,
  rating: 'up' | 'down' | null,
  options?: { comment?: string | null }
) => Promise<void>;

/**
 * Default ``onError`` for {@link useAgentAssistantMessageFeedback} — call with your UI toast/alert
 * function (e.g. ``p => toaster.create(p)``) so product and partner share the same copy.
 */
export function createAgentAssistantMessageFeedbackOnError(
  showError: (args: { type: 'error'; title: string; description: string }) => void
): (error: unknown) => void {
  return (error: unknown) => {
    const description =
      error instanceof Error && error.message.trim() ? error.message : 'Please try again.';
    showError({
      type: 'error',
      title: "Couldn't save feedback",
      description,
    });
  };
}

/** Whether to show thumbs for an assistant turn (stable ``msg_`` id and not the streaming tail bubble). */
export function agentAssistantThumbsVisible(options: {
  messageId: string;
  isLast: boolean;
  busy: boolean;
}): boolean {
  return options.messageId.startsWith('msg_') && !(options.isLast && options.busy);
}

type MaybeHttpishError = {
  status?: unknown;
  statusCode?: unknown;
  code?: unknown;
  response?: { status?: unknown };
} | null;

function getHttpStatus(error: unknown): number | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }
  const e = error as MaybeHttpishError;
  if (typeof e?.status === 'number') {
    return e.status;
  }
  if (typeof e?.statusCode === 'number') {
    return e.statusCode;
  }
  if (typeof e?.response?.status === 'number') {
    return e.response.status;
  }
  if (e?.code === 404) {
    return 404;
  }
  if (e?.code === '404') {
    return 404;
  }
  return undefined;
}

function isRetryableMessageNotFoundError(error: unknown): boolean {
  if (getHttpStatus(error) === 404) {
    return true;
  }
  if (error instanceof Error) {
    const msg = (error.message ?? '').toLowerCase();
    if (msg.includes('message not found') || msg.includes('http 404')) {
      return true;
    }
  }
  return false;
}

async function sleep(ms: number): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, ms));
}

export type UseAgentAssistantMessageFeedbackOptions = {
  message: UIMessage;
  submitMessageFeedback: AgentSubmitMessageFeedbackFn;
  setMessages: Dispatch<SetStateAction<UIMessage[]>>;
  onError?: (error: unknown) => void;
  /**
 * ``intent`` defers posting thumbs-down until the host calls {@link confirmThumbDown}
 * after the inline written-feedback step. **UI:** use {@link AgentAssistantWrittenFeedbackInline}
 * from this package in every shell (main + partner) — do not fork the card markup in app repos.
 * ``immediate`` posts on click (legacy; no written step).
 */
  thumbDownMode?: 'immediate' | 'intent';
  /** When ``thumbDownMode`` is ``intent``, invoked instead of posting when the user chooses thumbs down. */
  onThumbDownIntent?: (messageId: string) => void;
};

/**
 * Shared handler for Kin / advisor thumbs: guards duplicate submits, calls the agent
 * ``POST /v2/chat/feedback`` wrapper, then patches AI SDK message metadata optimistically.
 * Host apps supply ``onError`` (e.g. Chakra toaster) — this package stays UI-framework agnostic.
 */
export function useAgentAssistantMessageFeedback({
  message,
  submitMessageFeedback,
  setMessages,
  onError,
  thumbDownMode = 'immediate',
  onThumbDownIntent,
}: UseAgentAssistantMessageFeedbackOptions) {
  const messageId = message.id;
  /**
 * When the messageId matches, overrides `message` metadata for the thumbs
 * (covers useChat throttling / in-place mutation quirks). Tied to id so
 * display stays correct on the first render after a message swap.
 */
  const [optimisticFeedback, setOptimisticFeedback] = useState<{
    messageId: string;
    rating: 'up' | 'down' | null;
    comment: string | null;
  } | null>(null);
  const inFlightRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const displayRating =
    optimisticFeedback?.messageId === messageId
      ? optimisticFeedback.rating
      : advisorMessageFeedbackRating(message);

  const persistFeedback = useCallback(
    async (next: 'up' | 'down' | null, comment: string | null | undefined): Promise<boolean> => {
      if (!messageId.startsWith('msg_')) {
        return true;
      }
      if (inFlightRef.current) {
        return false;
      }
      inFlightRef.current = true;
      const trimmedForDown = next === 'down' ? (comment?.trim() ? comment.trim() : null) : null;
      const opts = next === 'down' ? { comment: trimmedForDown } : undefined;
      try {
        try {
          await submitMessageFeedback(messageId, next, opts);
        } catch (e) {
          if (isRetryableMessageNotFoundError(e)) {
            if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) {
              console.debug('[useAgentAssistantMessageFeedback] retrying after message not found', {
                messageId,
                next,
                error: e instanceof Error ? e.message : String(e),
              });
            }
            await sleep(450);
            await submitMessageFeedback(messageId, next, opts);
          } else {
            throw e;
          }
        }
        setOptimisticFeedback({ messageId, rating: next, comment: trimmedForDown });
        setMessages(prev =>
          prev.map(m =>
            m.id === messageId
              ? advisorUiMessageWithMetadataPatch(m, {
                  feedbackRating: next,
                  feedbackComment: next === 'down' ? trimmedForDown : null,
                })
              : m
          )
        );
        return true;
      } catch (e) {
        onErrorRef.current?.(e);
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [messageId, submitMessageFeedback, setMessages]
  );

  const onRate = useCallback(
    async (next: 'up' | 'down' | null): Promise<boolean> => {
      if (!messageId.startsWith('msg_')) {
        return true;
      }
      if (next === 'down' && thumbDownMode === 'intent') {
        if (onThumbDownIntent) {
          onThumbDownIntent(messageId);
          return true;
        }
        if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) {
          console.warn(
            '[useAgentAssistantMessageFeedback] thumbDownMode "intent" should set onThumbDownIntent; falling back to immediate POST'
          );
        }
      }
      return await persistFeedback(next, undefined);
    },
    [messageId, thumbDownMode, onThumbDownIntent, persistFeedback]
  );

  /** @returns Whether feedback was persisted (false on network/API failure or concurrent skip). */
  const confirmThumbDown = useCallback(
    async (comment: string | null): Promise<boolean> => {
      return await persistFeedback('down', comment);
    },
    [persistFeedback]
  );

  return { onRate, displayRating, confirmThumbDown };
}
